import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import * as estoqueApi from '../../api/estoque';
import * as pedidosApi from '../../api/pedidos';
import { useAuth } from '../../contexts/AuthContext';
import { useExigeEmpresaEspecifica } from '../../hooks/useExigeEmpresaEspecifica';
import AvisoSelecioneEmpresa from '../../components/common/AvisoSelecioneEmpresa';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { formatDate } from '../../utils/date';

function CardKpi({ icon, titulo, valor, to, variante = 'success', selo }) {
  const classe = {
    success: 'sf-kpi',
    warn: 'sf-kpi sf-kpi-warn',
    neutral: 'sf-kpi sf-kpi-neutral',
    dark: 'sf-kpi sf-kpi-dark',
  }[variante];
  const classeIcone = {
    success: 'sf-kpi-icon',
    warn: 'sf-kpi-icon sf-kpi-icon-warn',
    neutral: 'sf-kpi-icon sf-kpi-icon-neutral',
    dark: 'sf-kpi-icon',
  }[variante];

  return (
    <Link to={to} className={classe}>
      <div className="d-flex align-items-start justify-content-between gap-2">
        <span className={classeIcone}>
          <i className={`bi ${icon}`} />
        </span>
        {selo ? (
          <span className="sf-badge sf-badge-warn sf-badge-plain">{selo}</span>
        ) : (
          <i
            className="bi bi-arrow-up-right"
            style={{ fontSize: 15, color: variante === 'dark' ? 'rgba(255,255,255,0.5)' : '#c3c9bf' }}
          />
        )}
      </div>
      <div className="sf-kpi-label">{titulo}</div>
      <div className="sf-kpi-value">{valor}</div>
    </Link>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const { bloqueado, carregandoEmpresas, semEmpresaCadastrada } = useExigeEmpresaEspecifica();
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');
  const [estoque, setEstoque] = useState([]);
  const [rascunhos, setRascunhos] = useState([]);
  const [confirmadosHoje, setConfirmadosHoje] = useState([]);

  useEffect(() => {
    if (bloqueado) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setErro('');
    Promise.all([
      estoqueApi.listEstoque(),
      pedidosApi.listPedidos({ status: 'rascunho' }),
      pedidosApi.listPedidos({ status: 'confirmado', data_inicio: new Date().toISOString().slice(0, 10) }),
    ])
      .then(([e, r, c]) => {
        setEstoque(e.data);
        setRascunhos(r.data);
        setConfirmadosHoje(c.data);
      })
      .catch(() => setErro('Erro ao carregar o resumo do dia.'))
      .finally(() => setLoading(false));
  }, [bloqueado]);

  if (carregandoEmpresas || loading) return <LoadingSpinner />;

  const estoqueBaixo = estoque.filter((i) => Number(i.saldo_atual) <= 10).length;

  return (
    <div className="d-flex flex-column gap-4">
      <div>
        <h1 className="sf-page-title">Olá, {user?.nome?.split(' ')[0]}</h1>
        <span className="sf-page-sub">Resumo do dia — {formatDate(new Date().toISOString().slice(0, 10))}</span>
      </div>

      {bloqueado ? (
        <AvisoSelecioneEmpresa semEmpresaCadastrada={semEmpresaCadastrada} />
      ) : erro ? (
        <div className="sf-login-alert">
          <i className="bi bi-exclamation-circle" />
          {erro}
        </div>
      ) : (
        <>
          <div className="row g-3">
            <div className="col-6 col-lg-3">
              <CardKpi icon="bi-boxes" titulo="Itens em estoque" valor={estoque.length} to="/estoque" />
            </div>
            <div className="col-6 col-lg-3">
              <CardKpi
                icon="bi-exclamation-triangle"
                titulo="Estoque baixo (≤10)"
                valor={estoqueBaixo}
                to="/estoque"
                variante="warn"
                selo={estoqueBaixo > 0 ? 'ATENÇÃO' : null}
              />
            </div>
            <div className="col-6 col-lg-3">
              <CardKpi
                icon="bi-file-earmark-text"
                titulo="Pedidos em rascunho"
                valor={rascunhos.length}
                to="/pedidos"
                variante="neutral"
              />
            </div>
            <div className="col-6 col-lg-3">
              <CardKpi
                icon="bi-check-circle"
                titulo="Saídas confirmadas hoje"
                valor={confirmadosHoje.length}
                to="/pedidos"
                variante="dark"
              />
            </div>
          </div>

          <div className="row g-3">
            <div className="col-lg-6">
              <Link to="/pedidos/novo" className="sf-action-card sf-action-card-primary">
                <span className="sf-action-icon"><i className="bi bi-cart-plus" /></span>
                <span className="d-flex flex-column">
                  <span className="sf-action-title">Lançar saída (pedido)</span>
                  <span className="sf-action-sub">Separação e conferência</span>
                </span>
                <i className="bi bi-arrow-right ms-auto" style={{ fontSize: 19, color: 'rgba(255,255,255,0.75)' }} />
              </Link>
            </div>
            <div className="col-lg-6">
              <Link to="/lotes/novo" className="sf-action-card sf-action-card-ghost">
                <span className="sf-action-icon"><i className="bi bi-truck" /></span>
                <span className="d-flex flex-column">
                  <span className="sf-action-title">Registrar entrada (lote)</span>
                  <span className="sf-action-sub">Recebimento de fornecedor</span>
                </span>
                <i className="bi bi-arrow-right ms-auto" style={{ fontSize: 19, color: '#8b9382' }} />
              </Link>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
